import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'
import { type HttpClientError } from 'core/infra/http/HttpClient'

import { sentry } from 'legacy/utils/sentry'

import { type EmptyLocation } from '../../domain/shipment/EmptyLocation'
import {
  type TruckInfoPayload,
  type EmptyLocationPayload,
} from '../../infra/shipment/ShipmentsRepository'
import { getEmptyLocation } from '../../use-cases/shipment/getEmptyLocation'
import { saveEmptyLocation } from '../../use-cases/shipment/saveEmptyLocation'
import { updateShipmentTruckInfo } from '../../use-cases/shipment/updateShipmentTruckInfo'

export function useTruckInformation() {
  const [fetchEmptyLocationStatus, setFetchEmptyLocationStatus] = useState(RequestStatus.Initial)
  const [saveEmptyLocationStatus, setSaveEmptyLocationStatus] = useState(RequestStatus.Initial)
  const [updateTruckInfoStatus, setUpdateTruckInfoStatus] = useState(RequestStatus.Initial)

  const [emptyLocation, setEmptyLocation] = useState<undefined | EmptyLocation>()

  const isSavingEmptyLocation = saveEmptyLocationStatus === RequestStatus.Pending
  const isUpdatingTruckInfo = updateTruckInfoStatus === RequestStatus.Pending

  const savedEmptyLocation = saveEmptyLocationStatus === RequestStatus.Done

  const fetchEmptyLocationByShipmentId = useCallback(
    async (shipmentId: string, signal: AbortSignal) => {
      setFetchEmptyLocationStatus(RequestStatus.Pending)

      try {
        const foundEmptyLocation = await getEmptyLocation(shipmentId, { signal })

        if (!signal.aborted) {
          setEmptyLocation(foundEmptyLocation)
          setFetchEmptyLocationStatus(RequestStatus.Done)
        }
      } catch (e) {
        if (!signal.aborted) {
          // It doesn't need to throw an error because fetch could fail when there's no empty location
          setFetchEmptyLocationStatus(RequestStatus.Error)
        }
      }
    },
    []
  )

  const saveEmptyLocationByShipmentId = useCallback(
    async (emptyLocationPayload: EmptyLocationPayload) => {
      if (isSavingEmptyLocation) return
      setSaveEmptyLocationStatus(RequestStatus.Pending)

      try {
        const exists = emptyLocation !== undefined

        const savedEmptyLocation = await saveEmptyLocation({ ...emptyLocationPayload, exists })

        setEmptyLocation(savedEmptyLocation)
        setSaveEmptyLocationStatus(RequestStatus.Done)
      } catch (e) {
        const error = e as HttpClientError<Error>

        sentry.log(error)
        setSaveEmptyLocationStatus(RequestStatus.Error)

        throw new Error('Cannot add empty location', {
          cause: error,
        })
      }
    },
    [isSavingEmptyLocation, emptyLocation]
  )

  const updateTruckInfoByShipmentId = useCallback(async (truckInfo: TruckInfoPayload) => {
    setUpdateTruckInfoStatus(RequestStatus.Pending)

    try {
      await updateShipmentTruckInfo(truckInfo)
      setUpdateTruckInfoStatus(RequestStatus.Done)
    } catch (e) {
      const error = e as HttpClientError<Error>

      sentry.log(error)
      setUpdateTruckInfoStatus(RequestStatus.Error)

      throw new Error('Cannot update truck info on shipment', {
        cause: error,
      })
    }
  }, [])

  return {
    emptyLocation,
    isUpdatingTruckInfo,
    isSavingEmptyLocation,
    fetchEmptyLocationStatus,
    updateTruckInfoByShipmentId,
    saveEmptyLocationByShipmentId,
    fetchEmptyLocationByShipmentId,
    savedEmptyLocation,
  }
}
